import { NgStyle } from '@angular/common';
import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LocaleLanguage } from '@app/+authenticated/shared/locale/locale.helper';
import { Features } from '@app/enums';
import { FeatureFlagPipe } from '@app/pipes/feature-flag.pipe';
import { KnowledgeBaseArticleLinkPipe } from '@app/pipes/knowledge-base-article-link.pipe';
import { PlanCheckPipe } from '@app/pipes/plan-check.pipe';
import { TranslationParamsPipe } from '@app/pipes/translation-params.pipe';
import { DepartmentTargetAPI } from '@app/reducers/orm/departmentTarget/department-target.api';
import { InsightsService } from '@app/reducers/orm/insights/insights.service';
import { TrackingService } from '@app/services/tracking.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonThemePalette, DIALOG_DATA, DialogActivationComponent, DialogRef, InputModule } from '@sb/ui';
import { ModalBundle } from '@shiftbase-com/models';
import { finalize } from 'rxjs';

import { OnboardingDialogResult } from '../../../+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { KnowledgeBaseArticleLinkModule } from '../../../pipes/knowledge-base-article-link.module';
import { OnboardingChapterModule } from '../components/chapter.module';
import { TargetSettingSetupComponent } from './setup/target-setting-setup.component';
import { TargetSettingDialogStep } from './target-setting.model';

@Component({
  selector: 'target-setting-dialog',
  standalone: true,
  imports: [
    DialogActivationComponent,
    NgStyle,
    TranslateModule,
    OnboardingChapterModule,
    InputModule,
    KnowledgeBaseArticleLinkModule,
    TargetSettingSetupComponent,
    TranslationParamsPipe,
    ReactiveFormsModule,
    FeatureFlagPipe,
  ],
  providers: [PlanCheckPipe, FeatureFlagPipe],
  templateUrl: './target-setting-dialog.component.html',
})
export class TargetSettingDialogComponent implements OnInit {
  public ButtonThemePalette = ButtonThemePalette;

  public targetSettingDialogStep = TargetSettingDialogStep;
  public currentStep = TargetSettingDialogStep.TEASER;
  public steps = 7;

  public wagesSet = false;

  public formValues = {
    productivity: null,
    averageHourlyWage: null,
    laborCostPercentage: null,
  };

  public formGroup = new FormGroup(
    {
      productivity: new FormControl<string>(null, [Validators.min(0)]),
      averageHourlyWage: new FormControl<string>(null, [Validators.min(0)]),
      laborCostPercentage: new FormControl<string>(null, [Validators.min(0), Validators.max(100)]),
    },
    [
      (formGroup: FormGroup) => {
        const hasValues = Object.values(formGroup.value).some((value) => !!value);
        return hasValues ? null : { noValues: true };
      },
    ],
  );

  public currentYear = new Date().getFullYear();
  public eventTracked = false;

  public nextButtonLoading = false;

  public constructor(
    @Inject(DIALOG_DATA) public readonly eventData: ModalBundle,
    private readonly dialogRef: DialogRef<OnboardingDialogResult>,
    private insightsService: InsightsService,
    private destroyRef: DestroyRef,
    private knowledgeBaseArticleLinkPipe: KnowledgeBaseArticleLinkPipe,
    private departmentTargetApi: DepartmentTargetAPI,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    private trackingService: TrackingService,
    private router: Router,
    private featureFlagPipe: FeatureFlagPipe,
  ) {}

  public ngOnInit(): void {
    void this.insightsService
      .getOnboardingInsights()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((insights) => {
        this.wagesSet = insights.wagesFilledInPercentage >= 90;
      });
  }

  public goBack() {
    this.updateStep(this.currentStep - 1);
  }

  public onTargetSet(number: number, fieldName: string): void {
    this.formValues[fieldName] = number;
  }

  public goToStep(step: TargetSettingDialogStep) {
    if (this.currentStep === TargetSettingDialogStep.TEASER && !this.eventTracked) {
      this.trackingService.event('Activation Target Setting Started', this.eventData);
      this.eventTracked = true;
    }
    this.updateStep(step);
  }

  private updateStep(step: TargetSettingDialogStep) {
    this.currentStep = step;
    this.dialogRef.disableClose = step !== this.targetSettingDialogStep.TEASER;
  }

  public close() {
    this.dialogRef.close();

    if (this.featureFlagPipe.transform(Features.TMP_MONTHLY_TARGETS)) {
      this.router.navigateByUrl('/account/settings/targets');
    }
  }

  public savePreferencesAndGoToLastStep() {
    this.nextButtonLoading = true;
    const values = this.formGroup.value;

    void this.departmentTargetApi
      .setOnboardingTargets(values)
      .pipe(
        finalize(() => {
          this.nextButtonLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.updateStep(this.targetSettingDialogStep.SUMMARY);

        this.trackingService.event('Activation Target Setting Completed', this.eventData);
      });
  }

  public redirectToHelpCenter(url: string) {
    const link = this.knowledgeBaseArticleLinkPipe.transform(url);
    window.open(link, '_blank');
  }

  // english loom video serves as default
  private defaultLoomVideoId = 'eea90c9c7e7e42559dd9261ad96baaad?sid=b46b9492-73e8-4515-b516-a7a49ecbdbdd';
  private readonly loomVideoMap: Partial<Record<LocaleLanguage, string>> = {
    [LocaleLanguage.DUTCH]: '385640916dd24fd4b8b5280c9a3df7a0?sid=e1c4dc17-5168-4ffc-bca1-803956bf7efb',
    [LocaleLanguage.GERMAN]: '6d4f80b7e7a5416c93c9bcd34d278b83?sid=759ddff4-cd3e-489c-9158-de3e4bd9f284',
  };
  private loomVideoId: string = this.loomVideoMap[this.translate.currentLang] || this.defaultLoomVideoId;

  public loomVideoLink: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    `https://www.loom.com/embed/${this.loomVideoId}?hide_share=true&hide_speed=true&hide_title=true&hide_owner=true`,
  );
}
